import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  FileText, 
  DollarSign, 
  Package, 
  Users, 
  Truck, 
  ClipboardList, 
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Calendar,
  BarChart3,
  PieChart,
  AlertTriangle,
  Clock,
  Mail,
  Activity,
  Send,
  CheckCircle,
  FileSpreadsheet,
  Save,
  Edit2,
  Gift,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { HistoricalStockValues } from "@/components/HistoricalStockValues";
import SalesBySupplierModal from "@/components/reports/SalesBySupplierModal";
import { PaymentMixReport } from "@/components/reports/PaymentMixReport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Helper function to format currency
const formatCurrency = (value) => {
  return `R ${(parseFloat(value) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

// Helper function to get today's date
const getToday = () => new Date().toISOString().split("T")[0];

// Helper function to get first day of month
const getFirstOfMonth = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split("T")[0];
};

// Helper function to get first day of year
const getFirstOfYear = () => {
  const date = new Date();
  return new Date(date.getFullYear(), 0, 1).toISOString().split("T")[0];
};

// Helper to format time
const formatTime = (date) => {
  return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

// Report Card Component
const ReportCard = ({ title, description, icon: Icon, onClick, loading }) => (
  <Card 
    className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-purple-300"
    onClick={onClick}
  >
    <CardHeader className="pb-2">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-purple-100">
          <Icon className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <CardDescription className="text-xs">{description}</CardDescription>
        </div>
      </div>
    </CardHeader>
  </Card>
);

// Data Table Component
const DataTable = ({ columns, data, emptyMessage = "No data available" }) => {
  if (!data || data.length === 0) {
    return <div className="text-center py-8 text-gray-500">{emptyMessage}</div>;
  }
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100">
            {columns.map((col, i) => (
              <th key={i} className={`px-3 py-2 text-left font-medium ${col.align === 'right' ? 'text-right' : ''}`}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b hover:bg-gray-50">
              {columns.map((col, colIndex) => (
                <td key={colIndex} className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''}`}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Price List Download Card Component
const PriceListDownloadCard = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/reports/price-list/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().split("T")[0];
      link.download = `price_list_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Price list downloaded successfully");
    } catch {
      toast.error("Failed to download price list");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="border-2 border-green-200 bg-green-50">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <FileSpreadsheet className="h-6 w-6 text-green-700" />
            </div>
            <div>
              <div className="font-semibold text-green-900">Full Price List Export</div>
              <div className="text-sm text-green-700">All active products — PLU, barcode &amp; price incl. VAT</div>
            </div>
          </div>
          <Button
            data-testid="download-price-list-btn"
            onClick={handleDownload}
            disabled={downloading}
            className="bg-green-700 hover:bg-green-800 text-white"
          >
            {downloading ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Generating...</>
            ) : (
              <><Download className="h-4 w-4 mr-2" />Download Excel</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    green: "bg-green-50 text-green-600 border-green-200",
    red: "bg-red-50 text-red-600 border-red-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200"
  };
  
  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="h-4 w-4" />}
        <span className="text-xs font-medium uppercase">{title}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      {subtitle && <div className="text-xs opacity-75">{subtitle}</div>}
    </div>
  );
};

export default function Reports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("live");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [activeReport, setActiveReport] = useState(null);
  const [reportName, setReportName] = useState("");
  
  // Date filters
  const [startDate, setStartDate] = useState(getFirstOfMonth());
  const [endDate, setEndDate] = useState(getToday());
  const [singleDate, setSingleDate] = useState(getToday());
  const [deadStockMonths, setDeadStockMonths] = useState(12);
  const [paymentMixMin, setPaymentMixMin] = useState(3);
  
  // Live cash-up state
  const [liveCashUp, setLiveCashUp] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Email dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [salesBySupplierOpen, setSalesBySupplierOpen] = useState(false);
  
  // Fetch live cash-up data
  const fetchLiveCashUp = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/financial/cash-up`, {
        params: { date: getToday() }
      });
      setLiveCashUp(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching live cash-up:", error);
    }
  }, []);
  
  // Auto-refresh live cash-up every 30 seconds
  useEffect(() => {
    fetchLiveCashUp();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLiveCashUp, 30000); // 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchLiveCashUp]);
  
  // Fetch report data
  const fetchReport = async (endpoint, params = {}, name = "") => {
    setLoading(true);
    setReportName(name);
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `${API}/reports${endpoint}${queryParams ? `?${queryParams}` : ''}`;
      const response = await axios.get(url);
      setReportData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching report:", error);
      toast.error("Failed to fetch report");
      return null;
    } finally {
      setLoading(false);
    }
  };
  
  // Send report via email
  const handleSendEmail = async () => {
    if (!emailAddress || !reportData) {
      toast.error("Please enter an email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      const period = startDate && endDate ? `${startDate} to ${endDate}` : singleDate || getToday();
      
      await axios.post(`${API}/email/report`, {
        recipient_email: emailAddress,
        report_name: reportName || activeReport || "Report",
        report_data: reportData,
        period: period
      });
      
      toast.success(`Report sent to ${emailAddress}`);
      setEmailDialogOpen(false);
      setEmailAddress("");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email. Please check if email is configured.");
    } finally {
      setSendingEmail(false);
    }
  };

  // Report handlers
  const reports = {
    // Sales Reports
    dailySales: () => {
      setActiveReport("dailySales");
      fetchReport("/sales/daily", { date: singleDate }, "Daily Sales Report");
    },
    salesByPeriod: () => {
      setActiveReport("salesByPeriod");
      fetchReport("/sales/by-period", { start_date: startDate, end_date: endDate }, "Sales by Period Report");
    },
    salesByCategory: () => {
      setActiveReport("salesByCategory");
      fetchReport("/sales/by-category", { start_date: startDate, end_date: endDate }, "Sales by Category Report");
    },
    salesByCashier: () => {
      setActiveReport("salesByCashier");
      fetchReport("/sales/by-cashier", { start_date: startDate, end_date: endDate }, "Sales by Cashier Report");
    },
    topProducts: () => {
      setActiveReport("topProducts");
      fetchReport("/sales/top-products", { start_date: startDate, end_date: endDate, limit: 50 }, "Top Selling Products Report");
    },
    paymentMix: (minTxns = paymentMixMin) => {
      setActiveReport("paymentMix");
      setPaymentMixMin(minTxns);
      fetchReport("/sales/payment-mix", { start_date: startDate, end_date: endDate, min_txns: minTxns }, "Payment Mix by Product Report");
    },
    slowMoving: () => {
      setActiveReport("slowMoving");
      fetchReport("/sales/slow-moving", { days: 90 }, "Slow Moving Stock Report");
    },
    soldBelowCost: () => {
      setActiveReport("soldBelowCost");
      fetchReport("/sales/below-cost", { start_date: startDate, end_date: endDate }, "Sales Sold Below Cost Report");
    },
    
    // Financial Reports
    profitLoss: () => {
      setActiveReport("profitLoss");
      fetchReport("/financial/profit-loss", { start_date: startDate, end_date: endDate }, "Profit & Loss Report");
    },
    vatReport: () => {
      setActiveReport("vatReport");
      fetchReport("/financial/vat", { start_date: startDate, end_date: endDate }, "VAT Report");
    },
    cashUp: () => {
      setActiveReport("cashUp");
      fetchReport("/financial/cash-up", { date: singleDate }, "Cash-Up Report");
    },
    paymentMethods: () => {
      setActiveReport("paymentMethods");
      fetchReport("/financial/payment-methods", { start_date: startDate, end_date: endDate }, "Payment Methods Report");
    },
    
    // Stock Reports
    stockValuation: () => {
      setActiveReport("stockValuation");
      fetchReport("/stock/valuation", {}, "Stock Valuation Report");
    },
    stockValueBySupplier: () => {
      setActiveReport("stockValueBySupplier");
      fetchReport("/stock/valuation-by-supplier", {}, "Stock Value by Supplier Report");
    },
    stockMovement: () => {
      setActiveReport("stockMovement");
      fetchReport("/stock/movement", { start_date: startDate, end_date: endDate }, "Stock Movement Report");
    },
    reorderReport: () => {
      setActiveReport("reorderReport");
      fetchReport("/stock/reorder", {}, "Reorder Report");
    },
    deadStock: () => {
      setActiveReport("deadStock");
      fetchReport("/stock/dead-stock", { months: deadStockMonths }, "Dead Stock Report");
    },
    
    // Customer Reports
    customerSales: () => {
      setActiveReport("customerSales");
      fetchReport("/customers/sales", { start_date: startDate, end_date: endDate }, "Customer Sales Report");
    },
    customerAging: () => {
      setActiveReport("customerAging");
      fetchReport("/customers/aging", {}, "Customer Account Aging Report");
    },
    
    // Supplier Reports
    supplierPurchases: () => {
      setActiveReport("supplierPurchases");
      fetchReport("/suppliers/purchases", { start_date: startDate, end_date: endDate }, "Supplier Purchase Report");
    },
    supplierAging: () => {
      setActiveReport("supplierAging");
      fetchReport("/suppliers/aging", {}, "Supplier Account Aging Report");
    },
    
    // Audit
    auditLog: () => {
      setActiveReport("auditLog");
      fetchReport("/audit/transactions", { start_date: startDate, end_date: endDate, limit: 500 }, "Transaction Audit Log");
    }
  };

  // Re-fetch the active report when dates change
  useEffect(() => {
    if (!activeReport) return;
    const handler = reports[activeReport];
    if (handler) handler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, singleDate]);

  // Render report content based on active report
  const renderReportContent = () => {
    if (!reportData) return null;
    
    switch (activeReport) {
      case "dailySales":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Daily Sales Report - {reportData.date}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Sales" value={formatCurrency(reportData.summary?.total_sales_incl_vat)} icon={DollarSign} color="green" />
              <SummaryCard title="Transactions" value={reportData.summary?.transaction_count || 0} icon={FileText} color="blue" />
              <SummaryCard title="Items Sold" value={reportData.summary?.items_sold || 0} icon={Package} color="purple" />
              <SummaryCard title="Gross Profit" value={formatCurrency(reportData.summary?.gross_profit)} subtitle={`${reportData.summary?.gross_profit_percent || 0}%`} icon={TrendingUp} color="green" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <SummaryCard title="Cash" value={formatCurrency(reportData.payment_breakdown?.cash)} color="green" />
              <SummaryCard title="Card" value={formatCurrency(reportData.payment_breakdown?.card)} color="blue" />
              <SummaryCard title="Account" value={formatCurrency(reportData.payment_breakdown?.account)} color="orange" />
            </div>
            {reportData.hourly_breakdown && reportData.hourly_breakdown.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Hourly Breakdown</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "Hour", key: "hour" },
                      { header: "Transactions", key: "count", align: "right" },
                      { header: "Total", key: "total", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={reportData.hourly_breakdown}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "salesByPeriod":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Sales Report: {reportData.period?.start} to {reportData.period?.end}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Sales" value={formatCurrency(reportData.summary?.total_sales_incl_vat)} icon={DollarSign} color="green" />
              <SummaryCard title="Transactions" value={reportData.summary?.total_transactions || 0} icon={FileText} color="blue" />
              <SummaryCard title="Gross Profit" value={formatCurrency(reportData.summary?.gross_profit)} subtitle={`${reportData.summary?.gross_profit_percent || 0}%`} icon={TrendingUp} color="green" />
              <SummaryCard title="Avg Sale" value={formatCurrency(reportData.summary?.average_sale_value)} icon={BarChart3} color="purple" />
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Daily Breakdown</CardTitle></CardHeader>
              <CardContent>
                <DataTable 
                  columns={[
                    { header: "Date", key: "date" },
                    { header: "Transactions", key: "count", align: "right" },
                    { header: "Items", key: "items", align: "right" },
                    { header: "Total", key: "total", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.daily_breakdown}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "salesByCategory":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Sales by Category</h3>
            <Card>
              <CardHeader><CardTitle className="text-sm">By Category</CardTitle></CardHeader>
              <CardContent>
                <DataTable 
                  columns={[
                    { header: "Category", key: "category" },
                    { header: "Qty Sold", key: "qty", align: "right" },
                    { header: "Sales", key: "sales", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Cost", key: "cost", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Profit", key: "profit", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.by_category}
                />
              </CardContent>
            </Card>
            {reportData.by_department && reportData.by_department.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">By Department</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "Department", key: "department" },
                      { header: "Qty Sold", key: "qty", align: "right" },
                      { header: "Sales", key: "sales", align: "right", render: (v) => formatCurrency(v) },
                      { header: "Profit", key: "profit", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={reportData.by_department}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "salesByCashier":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Sales by Cashier</h3>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Cashier", key: "cashier" },
                    { header: "Transactions", key: "transaction_count", align: "right" },
                    { header: "Items", key: "items_sold", align: "right" },
                    { header: "Total Sales", key: "total_sales", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Cash", key: "cash_received", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Card", key: "card_received", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.cashiers}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "topProducts":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Top Selling Products</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-sm">Top by Quantity</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Qty", key: "qty_sold", align: "right" },
                      { header: "Sales", key: "total_sales", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={reportData.top_by_quantity?.slice(0, 20)}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm">Top by Value</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Sales", key: "total_sales", align: "right", render: (v) => formatCurrency(v) },
                      { header: "Profit", key: "gross_profit", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={reportData.top_by_value?.slice(0, 20)}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "slowMoving":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Slow Moving Stock (Last {reportData.period_days} days)</h3>
            <div className="grid grid-cols-2 gap-4">
              <SummaryCard title="Slow Moving Items" value={reportData.summary?.total_slow_movers || 0} icon={AlertTriangle} color="orange" />
              <SummaryCard title="Stock Value Tied Up" value={formatCurrency(reportData.summary?.total_stock_value)} icon={DollarSign} color="red" />
            </div>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "PLU", key: "plu_code" },
                    { header: "Product", key: "name" },
                    { header: "Stock", key: "current_stock", align: "right" },
                    { header: "Sold", key: "qty_sold", align: "right" },
                    { header: "Value", key: "stock_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Days Stock", key: "days_of_stock", align: "right" },
                    { header: "Last Sold", key: "last_sold" }
                  ]}
                  data={reportData.products?.slice(0, 50)}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "soldBelowCost":
        return (
          <div className="space-y-4" data-testid="sold-below-cost-report">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold">Sales Sold Below Cost ({reportData.summary?.period})</h3>
              <Button
                variant="outline"
                size="sm"
                data-testid="sold-below-cost-export-btn"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API}/reports/sales/below-cost/export?start_date=${startDate}&end_date=${endDate}`;
                  link.click();
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <SummaryCard title="Affected Lines" value={reportData.summary?.total_lines || 0} icon={AlertTriangle} color="red" />
              <SummaryCard title="Total Qty Sold Under Cost" value={reportData.summary?.total_qty || 0} icon={TrendingDown} color="orange" />
              <SummaryCard title="Total Loss (excl VAT)" value={formatCurrency(reportData.summary?.total_loss)} icon={DollarSign} color="red" />
            </div>
            <Card>
              <CardContent className="pt-4">
                {(!reportData.items || reportData.items.length === 0) ? (
                  <div className="text-center py-12 text-gray-500" data-testid="sold-below-cost-empty">
                    No sales below cost in this period.
                  </div>
                ) : (
                  <DataTable
                    columns={[
                      { header: "Date", key: "date" },
                      { header: "Trans #", key: "transaction_number" },
                      { header: "Cashier", key: "cashier" },
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Qty", key: "quantity", align: "right" },
                      { header: "Cost", key: "cost_excl_vat", align: "right", render: (v) => formatCurrency(v) },
                      { header: "Sold @ (excl)", key: "selling_excl_vat", align: "right", render: (v) => formatCurrency(v) },
                      { header: "Disc %", key: "discount_percent", align: "right", render: (v) => `${(v || 0).toFixed(1)}%` },
                      { header: "Loss R", key: "loss_amount", align: "right", render: (v) => <span className="text-red-600 font-semibold">{formatCurrency(v)}</span> },
                      { header: "Loss %", key: "loss_percent", align: "right", render: (v) => `${(v || 0).toFixed(1)}%` },
                      { header: "Customer", key: "customer" },
                    ]}
                    data={reportData.items}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "profitLoss":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Profit & Loss Statement</h3>
            <p className="text-sm text-gray-500">{reportData.period?.start} to {reportData.period?.end}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-sm text-green-700">Revenue</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Gross Revenue (Incl VAT)</span><span className="font-semibold">{formatCurrency(reportData.revenue?.gross_revenue_incl_vat)}</span></div>
                  <div className="flex justify-between text-red-600"><span>Less: Refunds</span><span>-{formatCurrency(reportData.revenue?.refunds)}</span></div>
                  <div className="flex justify-between border-t pt-2"><span>Net Revenue (Incl VAT)</span><span className="font-semibold">{formatCurrency(reportData.revenue?.net_revenue_incl_vat)}</span></div>
                  <div className="flex justify-between"><span>VAT Collected</span><span>{formatCurrency(reportData.revenue?.vat_collected)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-2"><span>Net Revenue (Excl VAT)</span><span>{formatCurrency(reportData.revenue?.net_revenue_excl_vat)}</span></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm text-blue-700">Profit Summary</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Cost of Goods Sold</span><span className="font-semibold">{formatCurrency(reportData.cost_of_goods_sold)}</span></div>
                  <div className="flex justify-between text-green-600 border-t pt-2"><span>Gross Profit</span><span className="font-bold">{formatCurrency(reportData.gross_profit)} ({reportData.gross_profit_percent}%)</span></div>
                  <div className="flex justify-between"><span>Less: Expenses</span><span className="text-red-600">-{formatCurrency(reportData.expenses?.total)}</span></div>
                  <div className={`flex justify-between font-bold border-t pt-2 ${reportData.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <span>Net Profit</span><span>{formatCurrency(reportData.net_profit)} ({reportData.net_profit_percent}%)</span>
                  </div>
                </CardContent>
              </Card>
            </div>
            {reportData.expenses?.by_category && Object.keys(reportData.expenses.by_category).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Expenses by Category</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "Category", key: "category" },
                      { header: "Amount", key: "amount", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={Object.entries(reportData.expenses.by_category).map(([k, v]) => ({ category: k, amount: v }))}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "vatReport":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">VAT Report for SARS</h3>
            <p className="text-sm text-gray-500">{reportData.period?.start} to {reportData.period?.end}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-green-200">
                <CardHeader><CardTitle className="text-sm text-green-700">Output VAT (Sales)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Total Sales (Incl VAT)</span><span>{formatCurrency(reportData.output_vat?.total_sales_incl_vat)}</span></div>
                  <div className="flex justify-between"><span>Standard Rated (15%)</span><span>{formatCurrency(reportData.output_vat?.standard_rated_sales)}</span></div>
                  <div className="flex justify-between"><span>Zero Rated</span><span>{formatCurrency(reportData.output_vat?.zero_rated_sales)}</span></div>
                  <div className="flex justify-between"><span>Exempt</span><span>{formatCurrency(reportData.output_vat?.exempt_sales)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-2"><span>VAT on Sales</span><span>{formatCurrency(reportData.output_vat?.vat_on_sales)}</span></div>
                </CardContent>
              </Card>
              <Card className="border-blue-200">
                <CardHeader><CardTitle className="text-sm text-blue-700">Input VAT (Purchases)</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Total Purchases (Incl VAT)</span><span>{formatCurrency(reportData.input_vat?.total_purchases_incl_vat)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-2"><span>VAT on Purchases</span><span>{formatCurrency(reportData.input_vat?.vat_on_purchases)}</span></div>
                </CardContent>
              </Card>
            </div>
            <Card className={reportData.vat_payable >= 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}>
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">{reportData.vat_status}</span>
                  <span className="text-2xl font-bold">{formatCurrency(Math.abs(reportData.vat_payable))}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "cashUp":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Cash-Up Report - {reportData.date}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="border-green-200">
                <CardHeader><CardTitle className="text-sm text-green-700">Cash Summary</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Cash In (Sales)</span><span className="text-green-600">+{formatCurrency(reportData.cash?.cash_in)}</span></div>
                  <div className="flex justify-between"><span>Petty Cash Out</span><span className="text-red-600">-{formatCurrency(reportData.cash?.petty_cash)}</span></div>
                  <div className="flex justify-between"><span>Refunds (Cash)</span><span className="text-red-600">-{formatCurrency(reportData.cash?.refunds_cash)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-2 text-lg"><span>Expected in Drawer</span><span>{formatCurrency(reportData.cash?.expected_in_drawer)}</span></div>
                </CardContent>
              </Card>
              <Card className="border-blue-200">
                <CardHeader><CardTitle className="text-sm text-blue-700">Other Payments</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between"><span>Card Payments</span><span>{formatCurrency(reportData.other_payments?.card)}</span></div>
                  <div className="flex justify-between"><span>Account Sales</span><span>{formatCurrency(reportData.other_payments?.account)}</span></div>
                  <div className="flex justify-between"><span>EFT</span><span>{formatCurrency(reportData.other_payments?.eft)}</span></div>
                  <div className="flex justify-between font-bold border-t pt-2"><span>Total Revenue</span><span>{formatCurrency(reportData.total_revenue)}</span></div>
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Transaction Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <SummaryCard title="Sales" value={reportData.transaction_breakdown?.sales?.count || 0} subtitle={formatCurrency(reportData.transaction_breakdown?.sales?.total)} color="green" />
                  <SummaryCard title="Refunds" value={reportData.transaction_breakdown?.refunds?.count || 0} subtitle={formatCurrency(reportData.transaction_breakdown?.refunds?.total)} color="red" />
                  <SummaryCard title="Petty Cash" value={reportData.transaction_breakdown?.petty_cash?.count || 0} subtitle={formatCurrency(reportData.transaction_breakdown?.petty_cash?.total)} color="orange" />
                  <SummaryCard title="Acc Payments" value={reportData.transaction_breakdown?.account_payments?.count || 0} subtitle={formatCurrency(reportData.transaction_breakdown?.account_payments?.total)} color="blue" />
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "paymentMix":
        return (
          <PaymentMixReport
            data={reportData}
            startDate={startDate}
            endDate={endDate}
            minTxns={paymentMixMin}
            onMinTxnsChange={(n) => reports.paymentMix(n)}
          />
        );

      case "paymentMethods":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Payment Methods Report</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reportData.by_method?.map((method, i) => (
                <SummaryCard 
                  key={i}
                  title={method.method} 
                  value={formatCurrency(method.total)} 
                  subtitle={`${method.percentage}% | ${method.count} transactions`}
                  color={method.method === 'CASH' ? 'green' : method.method === 'CARD' ? 'blue' : 'orange'}
                />
              ))}
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Daily Breakdown</CardTitle></CardHeader>
              <CardContent>
                <DataTable 
                  columns={[
                    { header: "Date", key: "date" },
                    { header: "Cash", key: "cash", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Card", key: "card", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Account", key: "account", align: "right", render: (v) => formatCurrency(v) },
                    { header: "EFT", key: "eft", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.daily_breakdown}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "stockValuation":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Stock Valuation Report</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Products" value={reportData.summary?.total_products || 0} icon={Package} color="blue" />
              <SummaryCard title="Total Qty" value={reportData.summary?.total_qty || 0} icon={Package} color="purple" />
              <SummaryCard title="Cost Value" value={formatCurrency(reportData.summary?.total_cost_value)} icon={DollarSign} color="orange" />
              <SummaryCard title="Potential Profit" value={formatCurrency(reportData.summary?.potential_gross_profit)} icon={TrendingUp} color="green" />
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Valuation by Category</CardTitle></CardHeader>
              <CardContent>
                <DataTable 
                  columns={[
                    { header: "Category", key: "category" },
                    { header: "Products", key: "product_count", align: "right" },
                    { header: "Qty", key: "total_qty", align: "right" },
                    { header: "Cost Value", key: "cost_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Retail Value", key: "retail_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Potential Profit", key: "potential_profit", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.by_category}
                />
              </CardContent>
            </Card>
            <HistoricalStockValues />
          </div>
        );

      case "stockValueBySupplier":
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Stock Value by Supplier Report</h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API}/reports/stock/valuation-by-supplier/export`;
                  link.download = `StockValueBySupplier_${new Date().toISOString().split('T')[0]}.xlsx`;
                  link.click();
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard title="Total Suppliers" value={reportData.summary?.total_suppliers || 0} icon={Users} color="purple" />
              <SummaryCard title="Products with Stock" value={reportData.summary?.total_products || 0} icon={Package} color="blue" />
              <SummaryCard title="Total Stock Value" value={formatCurrency(reportData.summary?.total_cost_value)} icon={DollarSign} color="orange" />
              <SummaryCard title="Potential Profit" value={formatCurrency(reportData.summary?.potential_gross_profit)} icon={TrendingUp} color="green" />
            </div>
            <Card>
              <CardHeader><CardTitle className="text-sm">Stock Value by Supplier</CardTitle></CardHeader>
              <CardContent>
                <DataTable 
                  columns={[
                    { header: "Supplier", key: "supplier" },
                    { header: "Products", key: "product_count", align: "right" },
                    { header: "Qty", key: "total_qty", align: "right" },
                    { header: "Cost Value", key: "cost_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Retail Value", key: "retail_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "% of Total", key: "percentage_of_total", align: "right", render: (v) => `${v}%` },
                    { header: "Potential Profit", key: "potential_profit", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.by_supplier}
                />
              </CardContent>
            </Card>
            {/* Show expandable product details for top suppliers */}
            {reportData.by_supplier?.slice(0, 5).map((supplier, idx) => (
              supplier.products?.length > 0 && (
                <Card key={idx} className="border-blue-200">
                  <CardHeader className="py-3 bg-blue-50">
                    <CardTitle className="text-sm flex justify-between">
                      <span>{supplier.supplier} - Top Products</span>
                      <span className="text-blue-600">{formatCurrency(supplier.cost_value)} ({supplier.percentage_of_total}%)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <DataTable 
                      columns={[
                        { header: "PLU", key: "plu_code" },
                        { header: "Product", key: "name" },
                        { header: "Category", key: "category" },
                        { header: "Stock", key: "stock", align: "right" },
                        { header: "Cost", key: "cost", align: "right", render: (v) => formatCurrency(v) },
                        { header: "Stock Value", key: "stock_value", align: "right", render: (v) => formatCurrency(v) }
                      ]}
                      data={supplier.products?.slice(0, 10)}
                    />
                    {supplier.products?.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Showing top 10 of {supplier.products.length} products. Export to Excel for full list.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )
            ))}
          </div>
        );

      case "stockMovement":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Stock Movement Report</h3>
            <div className="grid grid-cols-3 gap-4">
              <SummaryCard title="Products Moved" value={reportData.summary?.total_products || 0} icon={Package} color="blue" />
              <SummaryCard title="Total Stock In" value={reportData.summary?.total_stock_in || 0} icon={TrendingUp} color="green" />
              <SummaryCard title="Total Stock Out" value={reportData.summary?.total_stock_out || 0} icon={TrendingDown} color="red" />
            </div>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "PLU", key: "plu_code" },
                    { header: "Product", key: "name" },
                    { header: "Opening", key: "opening_stock", align: "right" },
                    { header: "In", key: "stock_in", align: "right", render: (v) => <span className="text-green-600">+{v}</span> },
                    { header: "Out", key: "stock_out", align: "right", render: (v) => <span className="text-red-600">-{v}</span> },
                    { header: "Closing", key: "closing_stock", align: "right" }
                  ]}
                  data={reportData.movements?.slice(0, 50)}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "reorderReport":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Reorder Report</h3>
            <div className="grid grid-cols-4 gap-4">
              <SummaryCard title="Critical Items" value={reportData.summary?.critical_count || 0} icon={AlertTriangle} color="red" />
              <SummaryCard title="Reorder Needed" value={reportData.summary?.reorder_count || 0} icon={Package} color="orange" />
              <SummaryCard title="Already On Order" value={reportData.summary?.excluded_count || 0} icon={Clock} color="gray" />
              <SummaryCard title="Est. Order Cost" value={formatCurrency(reportData.summary?.total_estimated_cost)} icon={DollarSign} color="blue" />
            </div>
            {reportData.critical?.length > 0 && (
              <Card className="border-red-300">
                <CardHeader><CardTitle className="text-sm text-red-700">🚨 Critical - Below Minimum Level</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Stock", key: "current_stock", align: "right" },
                      { header: "Min", key: "minimum_level", align: "right" },
                      { header: "Reorder", key: "reorder_level", align: "right" },
                      { header: "Suggested Qty", key: "suggested_order_qty", align: "right" },
                      { header: "Est. Cost", key: "estimated_cost", align: "right", render: (v) => formatCurrency(v) },
                      { header: "Supplier", key: "supplier" }
                    ]}
                    data={reportData.critical}
                  />
                </CardContent>
              </Card>
            )}
            {reportData.reorder_needed?.length > 0 && (
              <Card className="border-orange-300">
                <CardHeader><CardTitle className="text-sm text-orange-700">⚠️ Reorder Needed</CardTitle></CardHeader>
                <CardContent>
                  <DataTable 
                    columns={[
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Stock", key: "current_stock", align: "right" },
                      { header: "Reorder Lvl", key: "reorder_level", align: "right" },
                      { header: "Suggested Qty", key: "suggested_order_qty", align: "right" },
                      { header: "Est. Cost", key: "estimated_cost", align: "right", render: (v) => formatCurrency(v) }
                    ]}
                    data={reportData.reorder_needed?.slice(0, 50)}
                  />
                </CardContent>
              </Card>
            )}
            {reportData.excluded_already_ordered?.length > 0 && (
              <Card className="border-gray-300 bg-gray-50">
                <CardHeader><CardTitle className="text-sm text-gray-600">✓ Excluded - Already On Order ({reportData.excluded_already_ordered.length})</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-xs text-gray-500 mb-2">These products need reordering but already have stock on order</p>
                  <DataTable 
                    columns={[
                      { header: "PLU", key: "plu_code" },
                      { header: "Product", key: "name" },
                      { header: "Stock", key: "current_stock", align: "right" },
                      { header: "On Order", key: "stock_on_order", align: "right", render: (v) => <span className="text-blue-600 font-medium">{v}</span> },
                      { header: "Reorder Lvl", key: "reorder_level", align: "right" }
                    ]}
                    data={reportData.excluded_already_ordered?.slice(0, 30)}
                  />
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "deadStock":
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-lg font-semibold">Dead Stock Report</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  No sale AND no receiving for the chosen period. Any GRV or sale resets the clock.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-600 mr-1">Idle for at least:</Label>
                <Select
                  value={String(deadStockMonths)}
                  onValueChange={(v) => {
                    const m = parseInt(v, 10);
                    setDeadStockMonths(m);
                    fetchReport("/stock/dead-stock", { months: m }, "Dead Stock Report");
                  }}
                >
                  <SelectTrigger className="w-[130px]" data-testid="dead-stock-months-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="9">9 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="18">18 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                    <SelectItem value="36">36 months</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/reports/stock/dead-stock/xlsx`, {
                        params: { months: reportData.months_threshold || deadStockMonths },
                        responseType: "blob",
                      });
                      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `dead-stock-${reportData.months_threshold || deadStockMonths}mo-${new Date().toISOString().slice(0,10).replace(/-/g,"")}.xlsx`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Download started");
                    } catch {
                      toast.error("Download failed");
                    }
                  }}
                  data-testid="dead-stock-download-xlsx-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Excel
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <SummaryCard title={`Idle ${reportData.months_threshold}+ months`} value={reportData.summary?.total_products || 0} icon={AlertTriangle} color="red" />
              <SummaryCard title="Value Tied Up" value={formatCurrency(reportData.summary?.total_stock_value)} icon={DollarSign} color="orange" />
            </div>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "PLU", key: "plu_code" },
                    { header: "Product", key: "name" },
                    { header: "Category", key: "category" },
                    { header: "Stock", key: "current_stock", align: "right" },
                    { header: "Value", key: "stock_value", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Last Sold", key: "last_sold", render: (v) => v && v !== "Never" ? String(v).slice(0, 10) : "Never" },
                    { header: "Last Received", key: "last_received", render: (v) => v && v !== "—" ? String(v).slice(0, 10) : "—" },
                    { header: "Days Idle", key: "days_since_sale", align: "right" }
                  ]}
                  data={reportData.products?.slice(0, 100)}
                />
                {reportData.summary?.total_products > (reportData.products?.length || 0) && (
                  <p className="text-xs text-slate-500 mt-3">
                    Showing top {reportData.products?.length || 0} of {reportData.summary.total_products}. Download Excel above for the full list.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "customerSales":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Customer Sales Report</h3>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Customer", key: "customer_name" },
                    { header: "Account #", key: "account_number" },
                    { header: "Transactions", key: "transaction_count", align: "right" },
                    { header: "Items", key: "items_purchased", align: "right" },
                    { header: "Total Sales", key: "total_sales", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Avg Sale", key: "average_sale", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Profit", key: "gross_profit", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.customers}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "customerAging":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Customer Account Aging Report</h3>
            <div className="grid grid-cols-5 gap-4">
              <SummaryCard title="Current" value={formatCurrency(reportData.totals?.current)} color="green" />
              <SummaryCard title="30 Days" value={formatCurrency(reportData.totals?.days_30)} color="blue" />
              <SummaryCard title="60 Days" value={formatCurrency(reportData.totals?.days_60)} color="orange" />
              <SummaryCard title="90 Days" value={formatCurrency(reportData.totals?.days_90)} color="red" />
              <SummaryCard title="120+ Days" value={formatCurrency(reportData.totals?.days_120)} color="red" />
            </div>
            <Card className="border-purple-200">
              <CardContent className="pt-4 flex justify-between items-center">
                <span className="text-lg font-medium">Total Outstanding</span>
                <span className="text-2xl font-bold text-purple-600">{formatCurrency(reportData.grand_total)}</span>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Customer", key: "customer_name" },
                    { header: "Account #", key: "account_number" },
                    { header: "Contact", key: "contact" },
                    { header: "Current", key: "current", align: "right", render: (v) => formatCurrency(v) },
                    { header: "30 Days", key: "days_30", align: "right", render: (v) => formatCurrency(v) },
                    { header: "60 Days", key: "days_60", align: "right", render: (v) => formatCurrency(v) },
                    { header: "90 Days", key: "days_90", align: "right", render: (v) => formatCurrency(v) },
                    { header: "120+", key: "days_120_plus", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Total", key: "total_balance", align: "right", render: (v) => <span className="font-bold">{formatCurrency(v)}</span> }
                  ]}
                  data={reportData.customers}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "supplierPurchases":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Supplier Purchase Report</h3>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Supplier", key: "supplier_name" },
                    { header: "GRVs", key: "grv_count", align: "right" },
                    { header: "Items Received", key: "items_received", align: "right" },
                    { header: "Total Qty", key: "total_qty", align: "right" },
                    { header: "Total Cost", key: "total_cost", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Avg GRV", key: "average_grv_value", align: "right", render: (v) => formatCurrency(v) }
                  ]}
                  data={reportData.suppliers}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "supplierAging":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Supplier Account Aging (Accounts Payable)</h3>
            <div className="grid grid-cols-5 gap-4">
              <SummaryCard title="Current" value={formatCurrency(reportData.totals?.current)} color="green" />
              <SummaryCard title="30 Days" value={formatCurrency(reportData.totals?.days_30)} color="blue" />
              <SummaryCard title="60 Days" value={formatCurrency(reportData.totals?.days_60)} color="orange" />
              <SummaryCard title="90 Days" value={formatCurrency(reportData.totals?.days_90)} color="red" />
              <SummaryCard title="120+ Days" value={formatCurrency(reportData.totals?.days_120)} color="red" />
            </div>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-4 flex justify-between items-center">
                <span className="text-lg font-medium">Total Owed to Suppliers</span>
                <span className="text-2xl font-bold text-red-600">{formatCurrency(reportData.grand_total)}</span>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Supplier", key: "supplier_name" },
                    { header: "Contact", key: "contact" },
                    { header: "Terms", key: "payment_terms" },
                    { header: "Current", key: "current", align: "right", render: (v) => formatCurrency(v) },
                    { header: "30 Days", key: "days_30", align: "right", render: (v) => formatCurrency(v) },
                    { header: "60 Days", key: "days_60", align: "right", render: (v) => formatCurrency(v) },
                    { header: "90+", key: "days_90", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Total", key: "total_balance", align: "right", render: (v) => <span className="font-bold text-red-600">{formatCurrency(v)}</span> }
                  ]}
                  data={reportData.suppliers}
                />
              </CardContent>
            </Card>
          </div>
        );

      case "auditLog":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Audit Trail / Transaction Log</h3>
            <p className="text-sm text-gray-500">Showing {reportData.total_count} transactions</p>
            <Card>
              <CardContent className="pt-4">
                <DataTable 
                  columns={[
                    { header: "Date/Time", key: "datetime", render: (v) => v ? new Date(v).toLocaleString() : 'N/A' },
                    { header: "Type", key: "type" },
                    { header: "Reference", key: "reference" },
                    { header: "User", key: "user" },
                    { header: "Customer/Supplier", key: "customer" },
                    { header: "Items", key: "items", align: "right" },
                    { header: "Amount", key: "amount", align: "right", render: (v) => formatCurrency(v) },
                    { header: "Status", key: "status" }
                  ]}
                  data={reportData.transactions}
                />
              </CardContent>
            </Card>
          </div>
        );

      default:
        return <div className="text-center py-8 text-gray-500">Select a report to view</div>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500">Comprehensive business reporting</p>
        </div>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs">Single Date</Label>
              <Input 
                type="date" 
                value={singleDate} 
                onChange={(e) => setSingleDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="border-l pl-4">
              <Label className="text-xs">Date Range</Label>
              <div className="flex gap-2">
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
                <span className="self-center">to</span>
                <Input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setStartDate(getToday()); setEndDate(getToday()); }}>Today</Button>
              <Button variant="outline" size="sm" onClick={() => { setStartDate(getFirstOfMonth()); setEndDate(getToday()); }}>This Month</Button>
              <Button variant="outline" size="sm" onClick={() => { setStartDate(getFirstOfYear()); setEndDate(getToday()); }}>YTD</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="live" className="flex items-center gap-1"><Activity className="h-4 w-4" /> Live</TabsTrigger>
          <TabsTrigger value="sales" className="flex items-center gap-1"><BarChart3 className="h-4 w-4" /> Sales</TabsTrigger>
          <TabsTrigger value="financial" className="flex items-center gap-1"><DollarSign className="h-4 w-4" /> Financial</TabsTrigger>
          <TabsTrigger value="stock" className="flex items-center gap-1"><Package className="h-4 w-4" /> Stock</TabsTrigger>
          <TabsTrigger value="customers" className="flex items-center gap-1"><Users className="h-4 w-4" /> Customers</TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-1"><Truck className="h-4 w-4" /> Suppliers</TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-1"><ClipboardList className="h-4 w-4" /> Audit</TabsTrigger>
        </TabsList>

        {/* Live Cash-Up Dashboard */}
        <TabsContent value="live" className="space-y-4">
          <Card className="border-2 border-green-300 bg-gradient-to-r from-green-50 to-blue-50">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-green-500 animate-pulse">
                    <Activity className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Live Daily Cash-Up</CardTitle>
                    <CardDescription>
                      Today: {new Date().toLocaleDateString('en-ZA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-sm">
                    <div className="text-gray-500">Last updated</div>
                    <div className="font-medium">{lastUpdated ? formatTime(lastUpdated) : '--:--:--'}</div>
                  </div>
                  <Button 
                    variant={autoRefresh ? "default" : "outline"}
                    size="sm"
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={autoRefresh ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
                    {autoRefresh ? "Auto" : "Manual"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchLiveCashUp}>
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {liveCashUp ? (
                <div className="space-y-4">
                  {/* Day Closed Banner */}
                  {liveCashUp.day_closed && (
                    <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                        <div>
                          <div className="font-semibold text-green-800">Day Closed - Cash-Up Complete</div>
                          <div className="text-sm text-green-600">
                            Cash-Up #{liveCashUp.cashup_no} completed. All values reset to R 0.00 for new day.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Main Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white p-4 rounded-lg border-2 border-green-200 shadow-sm">
                      <div className="text-xs text-gray-500 uppercase font-medium">Total Revenue</div>
                      <div className="text-2xl font-bold text-green-600">{formatCurrency(liveCashUp.total_revenue)}</div>
                      <div className="text-xs text-gray-400 mt-1">Sales - Refunds - Petty Cash</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-purple-200 shadow-sm">
                      <div className="text-xs text-gray-500 uppercase font-medium">Total Money In</div>
                      <div className="text-2xl font-bold text-purple-600">{formatCurrency(liveCashUp.total_money_in)}</div>
                      <div className="text-xs text-gray-400 mt-1">Revenue + Acc Receipts</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-xs text-gray-500 uppercase font-medium">Sales Count</div>
                      <div className="text-2xl font-bold text-blue-600">{liveCashUp.transaction_breakdown?.sales?.count || 0}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border-2 border-blue-200 shadow-sm">
                      <div className="text-xs text-gray-500 uppercase font-medium">Expected Cash</div>
                      <div className="text-2xl font-bold text-blue-700">{formatCurrency(liveCashUp.cash?.expected_in_drawer)}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <div className="text-xs text-gray-500 uppercase font-medium">Petty Cash Out</div>
                      <div className="text-2xl font-bold text-orange-600">{formatCurrency(liveCashUp.cash?.petty_cash)}</div>
                    </div>
                  </div>

                  {/* Cash & Payment Breakdown */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        Cash Summary
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Petty Cash Out</span>
                          <span className="font-semibold text-red-600">-{formatCurrency(liveCashUp.cash?.petty_cash)}</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 mt-2">
                          <span className="font-medium">Expected in Drawer</span>
                          <span className="font-bold text-lg">{formatCurrency(liveCashUp.cash?.expected_in_drawer)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg border shadow-sm">
                      <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <PieChart className="h-4 w-4 text-blue-600" />
                        Payment Methods
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Card Payments</span>
                          <span className="font-semibold">{formatCurrency(liveCashUp.other_payments?.card)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Account Sales</span>
                          <span className="font-semibold">{formatCurrency(liveCashUp.other_payments?.account)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">EFT</span>
                          <span className="font-semibold">{formatCurrency(liveCashUp.other_payments?.eft)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transaction Breakdown */}
                  <div className="bg-white p-4 rounded-lg border shadow-sm">
                    <h3 className="font-semibold text-gray-700 mb-3">Transaction Breakdown</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{liveCashUp.transaction_breakdown?.sales?.count || 0}</div>
                        <div className="text-xs text-gray-500">Sales</div>
                        <div className="text-sm font-medium">{formatCurrency(liveCashUp.transaction_breakdown?.sales?.total)}</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{liveCashUp.transaction_breakdown?.refunds?.count || 0}</div>
                        <div className="text-xs text-gray-500">Refunds</div>
                        <div className="text-sm font-medium">{formatCurrency(liveCashUp.transaction_breakdown?.refunds?.total)}</div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">{liveCashUp.transaction_breakdown?.petty_cash?.count || 0}</div>
                        <div className="text-xs text-gray-500">Petty Cash</div>
                        <div className="text-sm font-medium">{formatCurrency(liveCashUp.transaction_breakdown?.petty_cash?.total)}</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{liveCashUp.transaction_breakdown?.account_payments?.count || 0}</div>
                        <div className="text-xs text-gray-500">Acc Payments</div>
                        <div className="text-sm font-medium">{formatCurrency(liveCashUp.transaction_breakdown?.account_payments?.total)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Email Button */}
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setReportData(liveCashUp);
                        setReportName("Live Cash-Up Report");
                        setEmailDialogOpen(true);
                      }}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email Cash-Up Report
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                  <span className="text-gray-500">Loading live data...</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Reports */}
        <TabsContent value="sales" className="space-y-4">
          {/* End of Day Report - amber featured card */}
          <button
            type="button"
            onClick={() => navigate("/reports/day-end")}
            data-testid="day-end-report-card"
            className="w-full text-left bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl p-5 shadow-md transition-all flex items-center justify-between group"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center text-2xl">
                📊
              </div>
              <div>
                <div className="text-lg font-bold">End of Day Report</div>
                <div className="text-amber-50 text-xs mt-0.5">
                  Turnover, GP, till banking & category performance · auto-emailed at 19:00 SAST
                </div>
              </div>
            </div>
            <div className="text-sm font-semibold opacity-80 group-hover:opacity-100">
              Open →
            </div>
          </button>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <ReportCard title="Daily Sales" description="Sales summary for a specific day" icon={Calendar} onClick={reports.dailySales} />
            <ReportCard title="Sales by Period" description="Custom date range sales" icon={BarChart3} onClick={reports.salesByPeriod} />
            <ReportCard title="Sales by Category" description="Breakdown by product category" icon={PieChart} onClick={reports.salesByCategory} />
            <ReportCard title="Sales by Cashier" description="Who sold what" icon={Users} onClick={reports.salesByCashier} />
            <ReportCard title="Top Selling Products" description="Best performers" icon={TrendingUp} onClick={reports.topProducts} />
            <ReportCard title="Payment Mix by Product" description="What sells more on card vs cash vs account" icon={PieChart} onClick={() => reports.paymentMix()} />
            <ReportCard title="Slow Moving Stock" description="Products not selling" icon={TrendingDown} onClick={reports.slowMoving} />
            <ReportCard title="Sold Below Cost" description="Lines where selling price was below cost" icon={AlertTriangle} onClick={reports.soldBelowCost} />
            <ReportCard title="Sales by Supplier" description="Qty sold per product for a supplier (This/Last month, 3M/6M/12M)" icon={Truck} onClick={() => setSalesBySupplierOpen(true)} />
          </div>
        </TabsContent>

        {/* Financial Reports */}
        <TabsContent value="financial" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ReportCard title="Profit & Loss" description="Revenue, costs, gross profit" icon={DollarSign} onClick={reports.profitLoss} />
            <ReportCard title="VAT Report" description="VAT for SARS submission" icon={FileText} onClick={reports.vatReport} />
            <ReportCard title="Cash-Up Report" description="Daily cash reconciliation" icon={DollarSign} onClick={reports.cashUp} />
            <ReportCard title="Payment Methods" description="Cash vs Card vs Account" icon={PieChart} onClick={reports.paymentMethods} />
            <ReportCard title="Payment Mix by Product" description="What sells more on card vs cash vs account" icon={PieChart} onClick={() => reports.paymentMix()} />
          </div>
        </TabsContent>

        {/* Stock Reports */}
        <TabsContent value="stock" className="space-y-4">
          {/* Price List Download - prominent card */}
          <PriceListDownloadCard />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ReportCard title="Stock Valuation" description="Total stock value at cost" icon={Package} onClick={reports.stockValuation} />
            <ReportCard title="Stock by Supplier" description="Stock value per supplier" icon={Truck} onClick={reports.stockValueBySupplier} />
            <ReportCard title="Stock Movement" description="In/out movements" icon={RefreshCw} onClick={reports.stockMovement} />
            <ReportCard title="Reorder Report" description="Products below reorder level" icon={AlertTriangle} onClick={reports.reorderReport} />
            <ReportCard title="Dead Stock" description="Products not sold in 6 months" icon={Clock} onClick={reports.deadStock} />
          </div>
        </TabsContent>

        {/* Customer Reports */}
        <TabsContent value="customers" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ReportCard title="Customer Sales" description="Sales per customer" icon={Users} onClick={reports.customerSales} />
            <ReportCard title="Account Aging" description="Outstanding balances by age" icon={Clock} onClick={reports.customerAging} />
          </div>
        </TabsContent>

        {/* Supplier Reports */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ReportCard title="Purchase History" description="What you bought from whom" icon={Truck} onClick={reports.supplierPurchases} />
            <ReportCard title="Supplier Aging" description="What you owe suppliers" icon={Clock} onClick={reports.supplierAging} />
          </div>
        </TabsContent>

        {/* Audit Reports */}
        <TabsContent value="audit" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ReportCard 
              title="Daily Transactions Export" 
              description="Download ALL transactions to Excel" 
              icon={FileSpreadsheet} 
              onClick={() => navigate('/reports/daily-transactions')} 
            />
            <ReportCard
              title="Voucher Audit"
              description="Spend-reward vouchers issued (per sale + summary)"
              icon={Gift}
              onClick={() => navigate('/reports/voucher-audit')}
            />
            <ReportCard
              title="Limpopo Sales Insights"
              description="AI-authored monthly trend report (your data + Limpopo market context)"
              icon={Sparkles}
              onClick={() => navigate('/reports/limpopo-insights')}
              data-testid="limpopo-insights-tile"
            />
            <ReportCard
              title="Card Reconciliation"
              description="Match card entries against your card-machine batch slip"
              icon={FileSpreadsheet}
              onClick={() => navigate('/reports/card-reconciliation')}
            />
            <ReportCard title="Transaction Log" description="All system activities" icon={ClipboardList} onClick={reports.auditLog} />
          </div>
        </TabsContent>
      </Tabs>

      {/* Report Results */}
      {loading ? (
        <Card>
          <CardContent className="py-16">
            <div className="flex flex-col items-center gap-4">
              <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
              <p className="text-gray-500">Loading report...</p>
            </div>
          </CardContent>
        </Card>
      ) : reportData && activeTab !== "live" && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">{reportName}</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setEmailDialogOpen(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Email Report
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {renderReportContent()}
          </CardContent>
        </Card>
      )}

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-purple-600" />
              Email Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Report</Label>
              <div className="text-sm text-gray-600 mt-1">{reportName || "Selected Report"}</div>
            </div>
            <div>
              <Label htmlFor="email">Send to Email Address</Label>
              <Input 
                id="email"
                type="email"
                placeholder="email@example.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="text-xs text-gray-500">
              The report will be formatted and sent as an HTML email with all the data displayed above.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailAddress}>
              {sendingEmail ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SalesBySupplierModal
        open={salesBySupplierOpen}
        onOpenChange={setSalesBySupplierOpen}
      />
    </div>
  );
}
